import { consoleLog } from '@Utils/consoleLog';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { decode as atob } from 'base-64';

import { prepareAuthenticateUserOrderRequest } from './checkout';
import type {
  AcceptPasswordResetResponse,
  AuthenticateResponse,
  AuthenticateUserOrder,
  AuthenticateUserOrderResponse,
  RequestPasswordResetResponse,
  SignupFromOrderResponse,
  SignupResponse,
} from './ownable.dtos';
import {
  AcceptPasswordReset,
  Authenticate,
  RequestPasswordReset,
  Signup,
  SignupFromOrder,
} from './ownable.dtos';
import ServiceStackGateway from './service-stack';

import type { SignupData, SignupFromOrderData } from '~types/gateways';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class AuthAPI {
  public static async AuthenticateUserOrder({
    orderId,
    saveAddress,
    saveAddressAs,
    saveCard,
    saveCardAs,
    setAddressAsDefault,
    setCardAsDefault,
  }: Omit<
    AuthenticateUserOrder,
    'createResponse' | 'getTypeName'
  >): Promise<AuthenticateUserOrderResponse | null> {
    if (!orderId) {
      return null;
    }
    try {
      const request = prepareAuthenticateUserOrderRequest({
        orderId,
        saveAddress,
        saveAddressAs,
        saveCard,
        saveCardAs,
        setAddressAsDefault,
        setCardAsDefault,
      });

      return await ServiceStackGateway.post<AuthenticateUserOrderResponse>(
        request
      );
    } catch (err) {
      consoleLog({ err });
      return null;
    }
  }

  public static async authCheckAsync(): Promise<any> {
    try {
      const request = new Authenticate();
      const authResponse = await ServiceStackGateway.get<AuthenticateResponse>(
        request
      );

      // Get roles
      const tokens = await AsyncStorage.getItem('tokens');
      let roles = [];
      if (tokens) {
        const parsedTokens = JSON.parse(tokens);
        const base64Url = parsedTokens?.bearer.split('.')[1];
        const userDetails = this.extractUserDetails(base64Url);
        roles = userDetails?.roles;
      }

      return {
        success: true,
        // eslint-disable-next-line radix
        id: Number.parseInt(authResponse.userId),
        name: authResponse.displayName,
        email: authResponse.userName,
        roles,
      };
    } catch (error) {
      consoleLog('error checking authentication', error);
      return null;
    }
  }

  private static extractUserDetails(base64Url: string) {
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      )
    );
  }

  public static async authCheckAsyncNative(tokens: {
    bearer: string;
    refresh: string;
  }): Promise<any> {
    try {
      const request = new Authenticate();
      const authResponse = await ServiceStackGateway.get<AuthenticateResponse>(
        request
      );

      const bearerToken = authResponse.bearerToken ?? tokens.bearer;
      const refreshToken = authResponse.refreshToken ?? tokens.refresh;

      const [_1, base64Url] = bearerToken.split('.');
      const userDetails = this.extractUserDetails(base64Url);

      return {
        success: true,
        // eslint-disable-next-line radix
        id: Number.parseInt(authResponse.userId),
        name: authResponse.displayName,
        email: userDetails.email,
        bearerToken,
        refreshToken,
      };
    } catch (error) {
      consoleLog('error', error);
      return null;
    }
  }

  // TODO: How to have IUser / IError returned downwards?
  public static async authenticateAsync(
    username: string,
    password: string
  ): Promise<any> {
    const request = new Authenticate();
    request.provider = 'credentials';
    request.userName = username;
    request.password = password;
    try {
      const authResponse = await ServiceStackGateway.post<AuthenticateResponse>(
        request
      );

      const [_0, base64Url] = authResponse.bearerToken.split('.');
      const userDetails = this.extractUserDetails(base64Url);

      return {
        success: true,
        // eslint-disable-next-line radix
        id: Number.parseInt(authResponse.userId),
        name: authResponse.displayName,
        email: authResponse.userName,
        bearerToken: authResponse.bearerToken,
        refreshToken: authResponse.refreshToken,
        roles: userDetails.roles,
      };
    } catch (error) {
      // @ts-expect-error
      return error.responseStatus;
    }
  }

  public static async logout(): Promise<void> {
    try {
      // [Dev note - SP - 19/Mar/2021] For some reason, using the logout class that's in the DTOS fails, but this appears to work
      await ServiceStackGateway.get(`/logout`).catch((e) => {
        consoleLog('API/API.ts -> logout() -> error: ', e);
      });

      await AsyncStorage.removeItem('tokens');
    } catch (e) {
      if (__DEV__) {
        consoleLog('API/API.ts -> logout() -> error: ', e);
      }
    }
  }

  public static SignupFromLogin({
    confirmPassword,
    emailAddress,
    fullName,
    password,
  }: SignupData): Promise<SignupResponse> {
    const request = new Signup();

    if (fullName) {
      request.fullName = fullName;
    }
    request.emailAddress = emailAddress;
    request.password = password;
    request.confirmPassword = confirmPassword;

    return ServiceStackGateway.post<SignupResponse>(request);
  }

  public static SignupFromOrder({
    confirmPassword,
    newPassword,
    orderId,
    orderToken,
  }: SignupFromOrderData): Promise<SignupFromOrderResponse> {
    const request = new SignupFromOrder();
    request.orderId = orderId;
    request.orderToken = orderToken;
    request.password = newPassword;
    request.confirmPassword = confirmPassword;

    return ServiceStackGateway.post<SignupFromOrderResponse>(request);
  }

  static async requestPasswordReset(email: string): Promise<void> {
    const request = new RequestPasswordReset();
    request.accountEmailAddress = email;
    const response =
      await ServiceStackGateway.post<RequestPasswordResetResponse>(request);

    if (!response.success) {
      throw new Error(response.errors[0]?.userMessage);
    }
  }

  static async acceptPasswordReset({
    password,
    confirmPassword,
    token,
  }: {
    password: string;
    confirmPassword: string;
    token: string;
  }): Promise<void> {
    const request = new AcceptPasswordReset();
    request.newPassword = password;
    request.confirmNewPassword = confirmPassword;
    request.token = token;

    const response =
      await ServiceStackGateway.post<AcceptPasswordResetResponse>(request);

    if (!response.success) {
      throw new Error(response.errors[0].userMessage);
    }

    if (!response.passwordChanged) {
      throw new Error(response.message);
    }
  }
}

export default AuthAPI;
